import React, { useEffect, useState, useRef } from "react";

import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import Headerbar from "../common/Headerbar";
import Breadcrumbs from "../common/Breadcrumbs";
import { EpisodesIcon } from "../common/icons";
import { audiogramsService } from "./services";
import { episodeService } from "../Episode/services";
import { usersService } from "../common/Headerbar/services";
import AudiogramCardGrid from "./parts/AudiogramCardGrid";
import GenerateCustomAudiogramModal from "./components/GenerateCustomAudiogramModal";
import CreditsWarningModal from "../common/CreditsWarningModal";
import { RoleName } from "../common/enums/RoleName";
import RoleBasedAccess from "../common/RoleBasedAccess";
import { trackEvent } from "../../analytics";

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
  bgColor?: string;
  borderColor?: string;
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  borderColor,
}: StatCardProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        maxWidth: { sm: "400px" },
        backgroundColor: bgColor ?? "#fff",
        border: "1px solid",
        borderColor: borderColor ?? "divider",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2 },
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: bgColor ? `${color}22` : `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: bgColor ? color : "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            color: bgColor ? color : "text.primary",
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Audiograms() {
  const { id: episodeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [creditsWarningModalOpen, setCreditsWarningModalOpen] = useState(false);
  const sidebarRef = useRef<SidebarRef>(null);

  const {
    data,
    refetch,
    isLoading,
    error: audiogramsError,
  } = useQuery({
    queryKey: ["getAudiograms", episodeId],
    queryFn: () => audiogramsService.getAudiograms(episodeId),
  });

  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
  });

  const { data: userData } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const availableCredits =
    userData?.organization?.subscription?.available_credits || 0;
  const hasCredits = availableCredits >= 5;

  const episodeName = episode?.name || "";

  const totalCount = data?.counts?.total ?? data?.audiograms?.length ?? 0;
  const readyCount = data?.counts?.ready ?? 0;
  const generatingCount = data?.counts?.generating ?? 0;

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();
      const elapsed = Date.now() - startTime;
      if (elapsed < 500) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 500 - elapsed);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleGenerateClick = () => {
    trackEvent("generate_audiogram_opened");
    setModalOpen(true);
  };

  // Open the custom-generate modal automatically when navigated here with
  // `?openGenerate=true` (e.g. from the episode page), then strip the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openGenerate") === "true") {
      setModalOpen(true);
      params.delete("openGenerate");
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SidebarView pageName={`Episode - ${episodeName}`} ref={sidebarRef} />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Headerbar
          pageName={`Episode - ${episodeName}`}
          onMobileMenuClick={handleMobileMenuToggle}
          headerLeftContent={
            <Breadcrumbs
              items={[
                {
                  label: "Episodes",
                  path: "/episodes",
                  icon: <EpisodesIcon sx={{ fontSize: 18 }} />,
                },
                {
                  label: episodeName || "Episode",
                  path: `/episodes/${episodeId}`,
                },
                {
                  label: "Audiograms",
                  path: `/episodes/${episodeId}/audiograms`,
                  icon: <GraphicEqIcon sx={{ fontSize: 18 }} />,
                },
              ]}
              showHome={false}
              inHeader
            />
          }
        />

        <Box
          sx={{
            flex: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            overflow: "auto",
            px: 3,
            py: 2,
          }}
        >
          {/* Stat cards */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <StatCard
              label="Total Audiograms"
              value={totalCount}
              icon={GraphicEqIcon}
              color="#7C6EF5"
            />
            <StatCard
              label="Ready"
              value={readyCount}
              icon={CheckCircleIcon}
              color="#2E7D32"
              bgColor="#E8F5E8"
              borderColor="#4CAF50"
            />
            <StatCard
              label="Generating"
              value={generatingCount}
              icon={AutorenewIcon}
              color="#E65100"
              bgColor="#FFF3E0"
              borderColor="#FFCC02"
            />
          </Box>

          {/* Toolbar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h6"
                component="h1"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                Audiograms
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Tooltip title="Refresh audiograms">
                <span>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    size="small"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "action.hover" },
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                      "& .MuiSvgIcon-root": isRefreshing
                        ? { animation: "spin 0.8s linear infinite" }
                        : {},
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
                <Button
                  variant="contained"
                  onClick={handleGenerateClick}
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  sx={{
                    backgroundColor: "#2563eb",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 0.875,
                    "&:hover": { backgroundColor: "#1d4ed8" },
                  }}
                >
                  Generate custom audiogram
                </Button>
              </RoleBasedAccess>
            </Box>
          </Box>

          {audiogramsError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "12px",
                border: "1px solid #fca5a5",
              }}
            >
              Failed to load audiograms. Please try again.
            </Alert>
          )}

          <Grid container spacing={2}>
            <AudiogramCardGrid
              isLoading={isLoading}
              onGenerateClick={handleGenerateClick}
              data={
                data || {
                  audiograms: [],
                  counts: { total: 0, ready: 0, generating: 0 },
                }
              }
              episodeId={episodeId || ""}
            />
          </Grid>
        </Box>
      </Box>

      {modalOpen && (
        <GenerateCustomAudiogramModal
          open={modalOpen}
          episodeId={episodeId || ""}
          audioUrl={episode?.upload?.url}
          hasCredits={hasCredits}
          onClose={() => setModalOpen(false)}
          onSuccessfullySaved={() => {
            setModalOpen(false);
            refetch();
          }}
          onCreditsExhausted={() => {
            setCreditsWarningModalOpen(true);
          }}
        />
      )}

      <CreditsWarningModal
        open={creditsWarningModalOpen}
        onClose={() => setCreditsWarningModalOpen(false)}
        onConfirm={() => {
          setCreditsWarningModalOpen(false);
          setModalOpen(true);
        }}
      />
    </Box>
  );
}
